import * as React from "react";
import { Filter, PropertyFiltersGroupProps } from "../typings";
import { FiltersContext } from "src/stores/PropertyFiltersStore/context/PropertyFiltersContext";
import { UitkExpandoList, UitkExpandoListItem } from "uitk-react-expando";
import { UitkSecondaryButton, UitkSecondaryFloatingButton } from "uitk-react-button";
import { UitkNotificationBadge1 } from "uitk-react-badge";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { useLocalization } from "@shared-ui/localization-context";
import { PropertyFilterInDialog } from "../dialog/PropertyFiltersDialog";
import {
  Action,
  FlexTrackingInfo,
  sendImmediateClickstreamTrackEvent,
  sendDelayedClickstreamTrackEvent,
} from "components/utility/analytics/FlexAnalyticsUtils";
import { PROPERTY_FILTERS } from "components/flexComponents/PropertyFilters/PropertyFilters";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";
import { useClickTracker } from "@shared-ui/clickstream-analytics-context";

export const DesktopPropertyFilters = () => {
  const context = React.useContext(FiltersContext);
  const track = useClickTracker();
  const { formatText } = useLocalization();
  const [isApplyButtonHidden, setIsApplyButtonHidden] = React.useState<boolean>(false);
  const [numberOfSelectedFilters, setNumberOfSelectedFilters] = React.useState<number>(
    context.getNumberOfSelectedFilters()
  );
  context.bindSetNumberOfSelectedFilters(setNumberOfSelectedFilters);

  const onClickApply = () => {
    const flexTracking: FlexTrackingInfo = {
      moduleName: PROPERTY_FILTERS,
      rfrr: showStickyButton ? `apply.sticky.${numberOfSelectedFilters}` : `apply.${numberOfSelectedFilters}`,
      action: Action.CLICK,
    };

    sendImmediateClickstreamTrackEvent(flexTracking, track);
    window.location.assign(context.buildLinkToSRP());
  };

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => {
        setIsApplyButtonHidden(e.intersectionRatio < 1);
      },
      {
        threshold: [1],
      }
    );

    observer.observe(document.querySelector(".DesktopPropertyFilters .applyContainer")!);
  }, []);

  const showStickyButton = isApplyButtonHidden && numberOfSelectedFilters > 0;
  const ApplyButton = showStickyButton ? UitkSecondaryFloatingButton : UitkSecondaryButton;
  const ButtonWrapper = showStickyButton ? UitkScrim : UitkLayoutFlexItem;

  return (
    <div className="DesktopPropertyFilters uitk-cell all-cell-1-1 all-t-padding-four uitk-card">
      <h2 className="desktopHeader uitk-type-bold uitk-type-500 all-t-padding-two all-b-padding-six all-l-padding-four">
        {formatText("propertyfilters.filterBy")}
      </h2>
      <UitkExpandoList className="all-r-padding-two">
        {context.getListOfFilters().map((filter, index) => (
          <PropertyFiltersGroup key={`${filter.name}-${index}`} filter={filter} context={context} />
        ))}
      </UitkExpandoList>
      <UitkLayoutFlex
        className={`applyContainer all-x-padding-twelve ${showStickyButton ? "sticky" : ""}`}
        alignItems="center"
        justifyContent="center"
      >
        <UitkLayoutFlexItem>
          <ButtonWrapper className="applyWrapper" type={UitkScrimType.BUTTON}>
            <ApplyButton
              className="applyButton"
              disabled={!numberOfSelectedFilters}
              onClick={onClickApply}
              text={formatText("propertyfilters.apply")}
              isFullWidth={true}
            >
              <UitkNotificationBadge1 text={String(numberOfSelectedFilters)} />
            </ApplyButton>
          </ButtonWrapper>
        </UitkLayoutFlexItem>
      </UitkLayoutFlex>
    </div>
  );
};

/**
 * Represents a filter group (e.g. Popular Filters)
 */
const PropertyFiltersGroup = (props: PropertyFiltersGroupProps) => {
  const { filter, context } = props;
  const track = useClickTracker();

  const [isOpen, setIsOpen] = React.useState(filter?.name === "popularFilter");

  if (!filter) {
    return null;
  }

  const callback = (filterNameAndShowMoreOrLess: string) => {
    if (filterNameAndShowMoreOrLess.includes(".ShowMore")) {
      setIsOpen(true);
    }

    if (filterNameAndShowMoreOrLess.includes(".ShowLess")) {
      setIsOpen(false);
    }

    const flexTracking: FlexTrackingInfo = {
      moduleName: PROPERTY_FILTERS,
      rfrr: filterNameAndShowMoreOrLess, // e.g. stars.ShowMore
      action: Action.CLICK,
    };
    sendDelayedClickstreamTrackEvent(flexTracking, track);
  };

  return (
    <UitkExpandoListItem
      expandoTitle={<PropertyFiltersGroupTitle {...filter} isOpen={isOpen} />}
      expandoDescription={<PropertyFilterInDialog context={context} filter={filter} withoutHeader={true} />}
      analytics={{
        id: filter!.name,
        callback,
      }}
      isVisible={filter!.name === "popularFilter"}
    />
  );
};

interface PropertyFiltersGroupTitleProps extends Pick<Filter, "label" | "name"> {
  isOpen: boolean;
}

const PropertyFiltersGroupTitle: React.FC<PropertyFiltersGroupTitleProps> = ({ label, name, isOpen }) => {
  const context = React.useContext(FiltersContext);

  if (!label || !name) {
    return null;
  }

  const totalFilters = context.getNumberOfSelectedFiltersPerCategory(name);

  return (
    <UitkLayoutFlex justifyContent="space-between">
      <UitkLayoutFlexItem>
        <div>{label}</div>
      </UitkLayoutFlexItem>
      {totalFilters !== 0 && !isOpen && (
        <UitkLayoutFlexItem>
          <div>
            <UitkNotificationBadge1 text={String(totalFilters)} />
          </div>
        </UitkLayoutFlexItem>
      )}
    </UitkLayoutFlex>
  );
};
